/**
 * KinsCircle Phase 6C — Lightweight semantic embedding adapter (V1).
 *
 * STABILITY.md §13:
 *   - V1 uses CHEAP, DETERMINISTIC, IN-MEMORY embeddings ONLY.
 *   - NO OpenAI embedding API calls.
 *   - NO external vector DB.
 *   - NO pgvector.
 *   - NO new paid infrastructure.
 *
 * The adapter implements a normalized weighted-token bag-of-features
 * vector, optionally boosted by tagged-member overlap and date proximity.
 * It is good enough for "find related memories", "smart search", and
 * "cluster Christmases together" without ever leaving the browser tab.
 */

export type EmbeddingVector = {
  /** Sparse term-frequency map, keyed by lowercased lemma. */
  terms: Record<string, number>;
  /** L2 norm of `terms`. Used to short-circuit cosine similarity. */
  norm: number;
  /** Normalized first-name set (e.g. `mom`, `cousin nia`). Used for relational boosts. */
  people: string[];
  /** Optional ISO year extracted from the source memory. */
  year: number | null;
};

export type SemanticMemoryRow = {
  id: string;
  caption: string;
  title?: string | null;
  date?: string | null;
  taggedNames?: string[];
  personId?: string | null;
};

export type SemanticSearchResult = {
  memoryId: string;
  score: number;
  matchedTerms: string[];
  matchedPeople: string[];
  yearProximity: number | null;
};

export type MemoryCluster = {
  id: string;
  label: string;
  memoryIds: string[];
  topTerms: string[];
  sharedPeople: string[];
  yearRange: { from: number | null; to: number | null };
};

export type RelationshipInsight = {
  memberKey: string;
  appearances: number;
  topTerms: string[];
  firstYear: number | null;
  lastYear: number | null;
  coAppearsWith: string[];
};

const STOPWORDS = new Set([
  'a',
  'an',
  'and',
  'are',
  'as',
  'at',
  'be',
  'by',
  'for',
  'from',
  'has',
  'had',
  'have',
  'he',
  'her',
  'his',
  'i',
  'in',
  'is',
  'it',
  'its',
  'me',
  'my',
  'of',
  'on',
  'or',
  'our',
  'she',
  'so',
  'that',
  'the',
  'their',
  'them',
  'they',
  'this',
  'to',
  'us',
  'was',
  'we',
  'were',
  'will',
  'with',
  'you',
  'your',
  'just',
  'very',
  'also',
  'than',
  'then',
  'there',
  'here',
  'when',
  'what',
  'who',
  'which',
  'how',
  'all',
  'some',
  'any',
  'one',
  'two',
  'too',
  'into',
  'out',
  'up',
  'down',
  'over',
  'about',
]);

/**
 * Light noun-phrase weighting: words frequently seen in family memories
 * get a small boost so clustering surfaces meaningful themes.
 */
const THEME_BOOST: Record<string, number> = {
  birthday: 1.6,
  wedding: 1.6,
  graduation: 1.6,
  reunion: 1.6,
  christmas: 1.6,
  thanksgiving: 1.6,
  easter: 1.4,
  hanukkah: 1.4,
  diwali: 1.4,
  ramadan: 1.4,
  beach: 1.3,
  vacation: 1.4,
  summer: 1.3,
  winter: 1.2,
  cookout: 1.5,
  barbecue: 1.5,
  basketball: 1.4,
  football: 1.4,
  baseball: 1.4,
  soccer: 1.4,
  dance: 1.3,
  recital: 1.4,
  baby: 1.5,
  newborn: 1.5,
  funeral: 1.3,
  memorial: 1.3,
  anniversary: 1.5,
  prom: 1.4,
  homecoming: 1.4,
  church: 1.3,
  family: 1.1,
  cousins: 1.2,
  grandma: 1.3,
  grandpa: 1.3,
  mom: 1.2,
  dad: 1.2,
};

const RELATIONAL_NOUNS = new Set([
  'mom',
  'mother',
  'dad',
  'father',
  'grandma',
  'grandmother',
  'grandpa',
  'grandfather',
  'auntie',
  'aunt',
  'uncle',
  'cousin',
  'cousins',
  'brother',
  'sister',
  'son',
  'daughter',
  'niece',
  'nephew',
  'spouse',
  'husband',
  'wife',
]);

const tokenize = (text: string): string[] => {
  if (!text) return [];
  return text
    .toLowerCase()
    .replace(/[^a-z0-9'\s-]/g, ' ')
    .split(/\s+/)
    .map((t) => t.trim())
    .filter((t) => t.length >= 2 && !STOPWORDS.has(t));
};

const extractYear = (date?: string | null): number | null => {
  if (!date) return null;
  const m = /(\d{4})/.exec(date);
  if (!m) return null;
  const y = parseInt(m[1], 10);
  if (!Number.isFinite(y) || y < 1850 || y > 2100) return null;
  return y;
};

const normalizePerson = (name: string): string =>
  name.trim().toLowerCase().replace(/\s+/g, ' ');

/**
 * Build a deterministic embedding vector for a single memory.
 * Pure: same input → same output, every time.
 */
export const generateMemoryEmbedding = (
  row: SemanticMemoryRow,
): EmbeddingVector => {
  const fullText = [row.title ?? '', row.caption ?? ''].join(' ');
  const tokens = tokenize(fullText);

  const terms: Record<string, number> = {};
  for (const t of tokens) {
    const boost = THEME_BOOST[t] ?? 1;
    terms[t] = (terms[t] ?? 0) + boost;
  }

  // Tagged member names get a strong, pre-tokenized weight.
  if (row.taggedNames && row.taggedNames.length) {
    for (const raw of row.taggedNames) {
      const k = `@${normalizePerson(raw)}`;
      terms[k] = (terms[k] ?? 0) + 2.5;
    }
  }

  // L2 norm.
  let sumSq = 0;
  for (const k of Object.keys(terms)) sumSq += terms[k] * terms[k];
  const norm = Math.sqrt(sumSq) || 1;

  return {
    terms,
    norm,
    people: (row.taggedNames ?? []).map(normalizePerson),
    year: extractYear(row.date),
  };
};

/** Standard cosine similarity between two sparse vectors. */
export const cosineSimilarity = (
  a: EmbeddingVector,
  b: EmbeddingVector,
): number => {
  if (!a || !b) return 0;
  if (!a.norm || !b.norm) return 0;
  const [shorter, longer] =
    Object.keys(a.terms).length < Object.keys(b.terms).length
      ? [a, b]
      : [b, a];
  let dot = 0;
  for (const k of Object.keys(shorter.terms)) {
    const v = longer.terms[k];
    if (v) dot += shorter.terms[k] * v;
  }
  return dot / (a.norm * b.norm);
};

/**
 * Compute a blended ranking score that combines pure cosine similarity
 * with relationship overlap and date proximity. All three signals are
 * already capped, so the final score is bounded in [0, 1.5).
 */
export const rankSemanticMatches = (
  query: EmbeddingVector,
  memories: { row: SemanticMemoryRow; vector: EmbeddingVector }[],
  opts: { topK?: number; minScore?: number } = {},
): SemanticSearchResult[] => {
  const topK = Math.max(1, Math.min(50, opts.topK ?? 20));
  const minScore = opts.minScore ?? 0.05;

  const out: SemanticSearchResult[] = [];
  for (const { row, vector } of memories) {
    const cos = cosineSimilarity(query, vector);
    if (cos < minScore) continue;

    // Relationship overlap boost.
    const sharedPeople = query.people.filter((p) =>
      vector.people.includes(p),
    );
    const peopleBoost = Math.min(0.4, sharedPeople.length * 0.15);

    // Date proximity boost.
    let yearProximity: number | null = null;
    let yearBoost = 0;
    if (query.year && vector.year) {
      const diff = Math.abs(query.year - vector.year);
      yearProximity = diff;
      if (diff <= 1) yearBoost = 0.12;
      else if (diff <= 5) yearBoost = 0.06;
    }

    // Matched plain-text terms (excluding @people and theme synonyms).
    const matchedTerms: string[] = [];
    for (const k of Object.keys(query.terms)) {
      if (k.startsWith('@')) continue;
      if (vector.terms[k]) matchedTerms.push(k);
    }

    out.push({
      memoryId: row.id,
      score: cos + peopleBoost + yearBoost,
      matchedTerms: matchedTerms.slice(0, 5),
      matchedPeople: sharedPeople.slice(0, 5),
      yearProximity,
    });
  }

  out.sort((a, b) => b.score - a.score);
  return out.slice(0, topK);
};

const dominantTermsFromGroup = (
  vectors: EmbeddingVector[],
  limit = 3,
): string[] => {
  const acc: Record<string, number> = {};
  for (const v of vectors) {
    for (const k of Object.keys(v.terms)) {
      if (k.startsWith('@')) continue;
      acc[k] = (acc[k] ?? 0) + v.terms[k];
    }
  }
  return Object.keys(acc)
    .filter((k) => !RELATIONAL_NOUNS.has(k))
    .sort((a, b) => acc[b] - acc[a])
    .slice(0, limit);
};

const labelForCluster = (terms: string[]): string => {
  if (!terms.length) return 'Family Memories';
  const first = terms[0];
  // Friendly capitalization for known themes.
  const known: Record<string, string> = {
    christmas: 'Christmas Memories',
    thanksgiving: 'Thanksgiving Gatherings',
    birthday: 'Birthday Celebrations',
    wedding: 'Weddings',
    graduation: 'Graduations',
    reunion: 'Family Reunions',
    summer: 'Summer Days',
    beach: 'Beach Trips',
    vacation: 'Family Vacations',
    cookout: 'Cookouts & Barbecues',
    barbecue: 'Cookouts & Barbecues',
    basketball: 'Basketball Memories',
    football: 'Football Memories',
    baseball: 'Baseball Memories',
    soccer: 'Soccer Memories',
    church: 'Church & Faith',
    anniversary: 'Anniversaries',
    baby: 'New Baby Moments',
  };
  if (known[first]) return known[first];
  return first.charAt(0).toUpperCase() + first.slice(1) + ' Memories';
};

/**
 * Greedy single-pass clustering. Memories whose cosine similarity to a
 * cluster's centroid exceeds 0.32 are absorbed; otherwise a new cluster
 * is opened. Cheap, deterministic, no ML dependency.
 */
export const clusterRelatedMemories = (
  rows: { row: SemanticMemoryRow; vector: EmbeddingVector }[],
  opts: { minClusterSize?: number; threshold?: number } = {},
): MemoryCluster[] => {
  const minSize = Math.max(2, opts.minClusterSize ?? 2);
  const threshold = opts.threshold ?? 0.32;

  type Bucket = {
    centroid: EmbeddingVector;
    members: { row: SemanticMemoryRow; vector: EmbeddingVector }[];
  };
  const buckets: Bucket[] = [];

  for (const item of rows) {
    let placed = false;
    for (const b of buckets) {
      if (cosineSimilarity(item.vector, b.centroid) >= threshold) {
        b.members.push(item);
        // Quick centroid refresh: average the term maps incrementally.
        const newTerms = { ...b.centroid.terms };
        for (const k of Object.keys(item.vector.terms)) {
          newTerms[k] = (newTerms[k] ?? 0) + item.vector.terms[k];
        }
        let sumSq = 0;
        for (const k of Object.keys(newTerms)) sumSq += newTerms[k] ** 2;
        b.centroid = {
          terms: newTerms,
          norm: Math.sqrt(sumSq) || 1,
          people: Array.from(
            new Set([...b.centroid.people, ...item.vector.people]),
          ),
          year: b.centroid.year ?? item.vector.year,
        };
        placed = true;
        break;
      }
    }
    if (!placed) {
      buckets.push({
        centroid: { ...item.vector },
        members: [item],
      });
    }
  }

  return buckets
    .filter((b) => b.members.length >= minSize)
    .map((b, i) => {
      const topTerms = dominantTermsFromGroup(
        b.members.map((m) => m.vector),
      );
      const years = b.members
        .map((m) => m.vector.year)
        .filter((y): y is number => typeof y === 'number');
      const sharedPeople = Array.from(
        new Set(b.members.flatMap((m) => m.vector.people)),
      ).slice(0, 6);
      return {
        id: `cluster_${i + 1}`,
        label: labelForCluster(topTerms),
        memoryIds: b.members.map((m) => m.row.id),
        topTerms,
        sharedPeople,
        yearRange: {
          from: years.length ? Math.min(...years) : null,
          to: years.length ? Math.max(...years) : null,
        },
      };
    })
    .sort((a, b) => b.memoryIds.length - a.memoryIds.length);
};

/**
 * Pull lightweight relationship signals (who appears most often, with
 * whom, and across what years) from a batch of indexed memories.
 *
 * STABILITY.md §13: this NEVER infers deaths/illnesses/crimes/awards
 * or any unstated fact — it only counts real co-appearances and tokens
 * already present in the captions the user wrote.
 */
export const extractRelationshipSignals = (
  rows: { row: SemanticMemoryRow; vector: EmbeddingVector }[],
  opts: { minAppearances?: number } = {},
): RelationshipInsight[] => {
  const minAppearances = Math.max(2, opts.minAppearances ?? 2);
  const acc: Record<
    string,
    {
      appearances: number;
      terms: Record<string, number>;
      years: number[];
      coPeople: Record<string, number>;
    }
  > = {};

  for (const { vector } of rows) {
    for (const p of vector.people) {
      if (!acc[p]) {
        acc[p] = { appearances: 0, terms: {}, years: [], coPeople: {} };
      }
      acc[p].appearances += 1;
      if (vector.year) acc[p].years.push(vector.year);
      for (const k of Object.keys(vector.terms)) {
        if (k.startsWith('@')) continue;
        if (RELATIONAL_NOUNS.has(k)) continue;
        acc[p].terms[k] = (acc[p].terms[k] ?? 0) + vector.terms[k];
      }
      for (const other of vector.people) {
        if (other === p) continue;
        acc[p].coPeople[other] = (acc[p].coPeople[other] ?? 0) + 1;
      }
    }
  }

  return Object.keys(acc)
    .map((memberKey) => {
      const e = acc[memberKey];
      const topTerms = Object.keys(e.terms)
        .sort((a, b) => e.terms[b] - e.terms[a])
        .slice(0, 4);
      const coAppearsWith = Object.keys(e.coPeople)
        .sort((a, b) => e.coPeople[b] - e.coPeople[a])
        .slice(0, 4);
      return {
        memberKey,
        appearances: e.appearances,
        topTerms,
        firstYear: e.years.length ? Math.min(...e.years) : null,
        lastYear: e.years.length ? Math.max(...e.years) : null,
        coAppearsWith,
      };
    })
    .filter((i) => i.appearances >= minAppearances)
    .sort((a, b) => b.appearances - a.appearances);
};

/**
 * Backwards-compatible internal helper for unit tests / sandbox calls.
 * Accepts a free-form text query and a roster of tagged names, returns
 * an `EmbeddingVector` ready to feed into `rankSemanticMatches`.
 */
export const embedQuery = (
  text: string,
  taggedNames: string[] = [],
): EmbeddingVector =>
  generateMemoryEmbedding({
    id: 'query',
    caption: text,
    title: null,
    date: null,
    taggedNames,
    personId: null,
  });
